import React, { useContext, useRef, useState } from 'react'
import { Box, Button, Typography, Dialog, DialogTitle, DialogContent, Stack, TextField } from '@mui/material';
import { useConfirmDialog } from './useConfirmDialog';
import { PostWorkImageUpload } from '../DataFetch/ManagementAPI';
import { useSuccessDialog } from './useSuccessDialog';
import { useErrorDialog } from './useErrorDialog';
import { SessionContext } from '../ManagementRoot';
import { ResizeImage } from '../Utility/ImageResize';

/**
 * 作品画像アップロードダイアログHook。
 * 指定された作品IDの作品画像を更新するダイアログを表示する。
 * @param artworkId 作品ID
 */
export function useWorkImageUploadDialog(artworkId:string | undefined) {

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [isComplete, setIsComplete] = useState<boolean>(false);

    const {waitConfirm, ConfirmDialog} = useConfirmDialog();

    const {openSuccessDialog, SuccessDialog} = useSuccessDialog();

    const {openErrorDialog, ErrorDialog} = useErrorDialog();

    const ConfirmSession = useContext(SessionContext);

    const fileRef = useRef<HTMLInputElement>(null);

    function OpenDialog() {
        setIsOpen(true);
        setIsComplete(false);
    }

    async function ImageUpload() {

        if(!await ConfirmSession()) {
            return;
        }

        const file:File | undefined = fileRef.current?.files?.[0]

        if(typeof file === "undefined") {
            return;
        }

        var resizedImage = await ResizeImage(file);
        
        setIsOpen(false);  

        await waitConfirm("作品データを更新します。よろしいですか？")
        .then(res => {
            res && PostWorkImageUpload({
                artworkId: artworkId,
                imageFile: resizedImage,
            })
            .then(res => {
                if(res) {
                    openSuccessDialog();
                    setIsOpen(false);
                    setIsComplete(true);
                } else {
                    openErrorDialog();
                }
            })
        })
    }

    function UploadDialog() {

        return(
            <>
                <Dialog
                    open={isOpen}
                    onClose={() => setIsOpen(false)}
                >
                    <DialogTitle>
                        <Typography>
                            作品データ更新
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" m={2}>
                            作品画像データを更新します。<br/>
                            4000x3000以上の画像は縮小されます。<br/>
                            変更した画像は元に戻せません。
                        </Typography>
                        <Stack spacing={2}>
                            <Box>作品画像データ</Box>
                            <Box>                                    
                                <TextField 
                                    type="file" 
                                    fullWidth
                                    inputRef={fileRef}
                                    inputProps={{accept:"image/jpeg,image/png"}}
                                />
                            </Box>
                            <Box>
                                <Button variant="contained" onClick={ImageUpload}> 
                                    更新
                                </Button>
                            </Box>
                        </Stack>
                    </DialogContent>
                </Dialog>

                <ConfirmDialog/>
                <SuccessDialog/>
                <ErrorDialog/>
            </>
        );
    }

    return {
        /**
         * 実行すると作品画像アップロードダイアログが表示される
         */
        openUploadDialog: OpenDialog,

        /**
         * ダイアログを表示する画面要素のどこかに配置する
         */
        UploadDialog: UploadDialog,

        /**
         * アップロード完了フラグ
         */
        isComplete: isComplete
    }
}